import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Blocks, Mail, Plug, RotateCcw, Send, type IconComponent } from '@/components/icons';
import browserstackLogo from '@/assets/brands/browserstack.svg';
import gitlabLogo from '@/assets/brands/gitlab.svg';
import jiraLogo from '@/assets/brands/jira.svg';
import teamsLogo from '@/assets/brands/microsoft-teams.svg';
import slackLogo from '@/assets/brands/slack.svg';
import cliqLogo from '@/assets/brands/zoho-cliq.svg';
import { isNotifyChannel } from '@1qa/shared';
import { NotifyChannelEditor } from '@/components/NotifyChannelEditor';
import { z } from 'zod';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Plugin } from '@/lib/types';
import { ScopePicker } from '@/components/ScopePicker';
import { useScope } from '@/lib/scope';
import { Badge, Button, Card, Dialog, EmptyState, ErrorNotice, Field, Input, PageHeader, PageLoader, Textarea, Toggle, useToast } from '@/components/ui';

/**
 * Known integrations shown even before they are configured (see docs/architecture.md "Plugins").
 * `logo` is the product's official mark (src/assets/brands, sources noted in each file); Email and custom plugins use an icon.
 */
const CATALOG: { key: string; name: string; description: string; logo?: string; Icon: IconComponent }[] = [
  { key: 'slack', name: 'Slack', description: 'Tell a Slack channel when a scheduled or webhook run fails.', logo: slackLogo, Icon: Plug },
  { key: 'teams', name: 'Microsoft Teams', description: 'Tell a Teams channel when a scheduled or webhook run fails.', logo: teamsLogo, Icon: Plug },
  { key: 'zoho-cliq', name: 'Zoho Cliq', description: 'Tell a Cliq channel when a scheduled or webhook run fails.', logo: cliqLogo, Icon: Plug },
  { key: 'email', name: 'Email', description: 'Email your team when a scheduled or webhook run fails.', Icon: Mail },
  { key: 'jira', name: 'Jira', description: 'Create issues from failed tests.', logo: jiraLogo, Icon: Plug },
  { key: 'gitlab', name: 'GitLab', description: 'Trigger runs from pipelines and report back to merge requests.', logo: gitlabLogo, Icon: Plug },
  { key: 'browserstack', name: 'BrowserStack', description: 'Run tests on real devices in the cloud.', logo: browserstackLogo, Icon: Plug },
];

const configSchema = z.record(z.unknown());

/** A catalog entry as shown in the current scope: the scope's own setting, or the organization's it inherits. */
type PluginRow = Plugin & { description: string; logo?: string; Icon: IconComponent; inherited: boolean };

const blank = (key: string, name: string, orgId: string): Plugin => ({
  id: '',
  orgId,
  projectId: null,
  key,
  name,
  enabled: false,
  config: {},
  secretRef: null,
});

function PluginEditor({
  plugin,
  orgId,
  projectId,
  open,
  onClose,
}: {
  plugin: Plugin;
  orgId: string;
  projectId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [json, setJson] = useState('');
  const [secretRef, setSecretRef] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setJson(JSON.stringify(plugin.config ?? {}, null, 2));
      setSecretRef(plugin.secretRef ?? '');
      setError(null);
    }
  }, [open, plugin]);

  const save = useMutation({
    mutationFn: (config: Record<string, unknown>) =>
      api.upsertPlugin(orgId, plugin.key, { name: plugin.name, enabled: plugin.enabled, config, secretRef: secretRef.trim() || null, projectId }),
    onSuccess: () => {
      toast.success(`${plugin.name} settings saved`);
      qc.invalidateQueries({ queryKey: ['plugins', orgId] });
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const submit = () => {
    try {
      const parsed = configSchema.parse(JSON.parse(json || '{}'));
      setError(null);
      save.mutate(parsed);
    } catch {
      setError('Configuration must be a valid JSON object, e.g. { "channel": "#qa" }');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Configure ${plugin.name}`}
      description="Settings are stored as JSON. Never put secrets here - reference an environment variable instead."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field label="Configuration (JSON)" error={error}>
          {(id) => (
            <Textarea id={id} value={json} onChange={(e) => setJson(e.target.value)} rows={10} spellCheck={false} className="font-mono text-xs" />
          )}
        </Field>
        <Field label="Secret reference" hint="Name of the environment variable or vault key that holds the token, e.g. SLACK_WEBHOOK_URL.">
          {(id) => <Input id={id} value={secretRef} onChange={(e) => setSecretRef(e.target.value)} placeholder="SLACK_WEBHOOK_URL" className="font-mono" />}
        </Field>
      </div>
    </Dialog>
  );
}

/** A notification channel has what it needs to send (webhook URL, or email settings). */
function notifyReady(p: Plugin): boolean {
  if (p.key === 'email') return Array.isArray(p.config?.recipients) && (p.config.recipients as unknown[]).length > 0 && typeof p.config.host === 'string';
  return !!p.hasSecret;
}

function notifySummary(p: Plugin): string {
  if (!notifyReady(p)) return 'Not set up yet';
  if (p.key === 'email') {
    const n = (p.config.recipients as unknown[]).length;
    return `Emails ${n} recipient${n === 1 ? '' : 's'} via ${String(p.config.host)}`;
  }
  return 'Webhook URL saved';
}

export function PluginsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { currentOrgId, memberships } = useAuth();
  const orgId = currentOrgId ?? '';
  const orgName = memberships.find((m) => m.orgId === orgId)?.orgName;
  const scope = useScope(currentOrgId);
  const plugins = useQuery({ queryKey: ['plugins', orgId], queryFn: () => api.plugins(orgId), enabled: !!orgId });
  const [editing, setEditing] = useState<Plugin | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['plugins', orgId] });

  const toggle = useMutation({
    mutationFn: (p: Plugin) =>
      api.upsertPlugin(orgId, p.key, { name: p.name, enabled: !p.enabled, config: p.config ?? {}, secretRef: p.secretRef ?? null, projectId: scope.projectId }),
    onSuccess: (p) => {
      toast.success(`${p.name} ${p.enabled ? 'enabled' : 'disabled'}${scope.project ? ` for ${scope.project.name}` : ''}`);
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const sendTest = useMutation({
    mutationFn: (p: Plugin) => api.testPlugin(p.id),
    onSuccess: (_r, p) => toast.success(`Test message sent to ${p.name} - check that it arrived`),
    onError: (e) => toast.error(errorMessage(e)),
  });

  const reset = useMutation({
    mutationFn: (p: Plugin) => api.deletePlugin(p.id),
    onSuccess: () => {
      toast.success('Now using the organization setting');
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (!orgId) return <EmptyState icon={<Plug className="size-6" />} title="No organization selected" description="Create an organization first." />;
  if (plugins.isPending) return <PageLoader />;
  if (plugins.isError) return <ErrorNotice error={plugins.error} onRetry={() => plugins.refetch()} />;

  const orgRows = new Map(plugins.data.filter((p) => p.projectId === null).map((p) => [p.key, p]));
  const scopeRows = new Map(plugins.data.filter((p) => p.projectId === scope.projectId).map((p) => [p.key, p]));
  const keys = [...new Set([...CATALOG.map((c) => c.key), ...orgRows.keys(), ...scopeRows.keys()])];
  const rows: PluginRow[] = keys.map((key) => {
    const meta = CATALOG.find((c) => c.key === key);
    const own = scopeRows.get(key);
    const inheritedFrom = scope.projectId ? orgRows.get(key) : undefined;
    const base = own ?? inheritedFrom ?? blank(key, meta?.name ?? key, orgId);
    return { ...base, description: meta?.description ?? 'Custom integration', logo: meta?.logo, Icon: meta?.Icon ?? Blocks, inherited: !own && !!inheritedFrom };
  });

  return (
    <div>
      <PageHeader
        eyebrow={orgName ?? 'Organization settings'}
        title="Plugins"
        description="Connect 1QA to the tools your team already uses - for the whole organization, or with different settings for one project."
      />
      <div className="mb-6">
        <ScopePicker projects={scope.projects} projectId={scope.projectId} onChange={scope.setProjectId} />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {rows.map((p) => (
          <Card key={p.key} className="flex flex-col p-5">
            <div className="flex items-start gap-4">
              <span className="grid size-11 shrink-0 place-items-center rounded-card bg-surface-sunken text-fg-secondary ring-1 ring-border ring-inset">
                {p.logo ? <img src={p.logo} alt="" className="size-6 object-contain" /> : <p.Icon className="size-5" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-fg">{p.name}</h3>
                  {p.enabled ? <Badge tone="success">Enabled</Badge> : <Badge>Off</Badge>}
                  {p.inherited && <Badge tone="info">From organization</Badge>}
                  {scope.projectId && !p.inherited && p.id && <Badge tone="brand">Project setting</Badge>}
                </div>
                <p className="mt-0.5 text-sm text-fg-secondary">{p.description}</p>
              </div>
              <Toggle
                label={`${p.enabled ? 'Disable' : 'Enable'} ${p.name}${scope.project ? ` for ${scope.project.name}` : ''}`}
                checked={p.enabled}
                disabled={toggle.isPending}
                // A notification channel needs its webhook / email settings before it can be turned on.
                onChange={() => (isNotifyChannel(p.key) && !p.enabled && !notifyReady(p) ? setEditing(p) : toggle.mutate(p))}
              />
            </div>
            <div className="mt-4 flex items-center justify-between gap-2 border-t border-border-subtle pt-4">
              {isNotifyChannel(p.key) ? (
                <span className="truncate text-xs text-fg-muted">{notifySummary(p)}</span>
              ) : (
                <span className="truncate font-mono text-xs text-fg-muted">{p.secretRef ? `secret: ${p.secretRef}` : 'no secret configured'}</span>
              )}
              <div className="flex gap-1">
                {isNotifyChannel(p.key) && p.id && notifyReady(p) && (
                  <Button size="sm" variant="ghost" loading={sendTest.isPending && sendTest.variables?.id === p.id} onClick={() => sendTest.mutate(p)} icon={<Send className="size-3" />}>
                    Send test
                  </Button>
                )}
                {scope.projectId && !p.inherited && p.id && (
                  <Button size="sm" variant="ghost" loading={reset.isPending} onClick={() => reset.mutate(p)} icon={<RotateCcw className="size-3" />}>
                    Use organization setting
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setEditing(p)} icon={<Plug className="size-3" />}>
                  Configure
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {editing && isNotifyChannel(editing.key) && (
        <NotifyChannelEditor plugin={editing} channel={editing.key} orgId={orgId} projectId={scope.projectId} onClose={() => setEditing(null)} />
      )}
      {editing && !isNotifyChannel(editing.key) && <PluginEditor plugin={editing} orgId={orgId} projectId={scope.projectId} open={!!editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
